// @ts-nocheck
import * as React from "react";
import { Ic, apiErrorMessage } from "../screens/common";
import { useEscapeKey } from "./useEscapeKey";
import { getRecentReasons, pushRecentReason } from "./recentReasons";

// "이 매니저에게 케이스 배정" picker. Loads cases in the manager's region
// that are still in the matching pool (no manager + status=matching), and
// PATCHes the chosen case with the manager id. The backend bumps the
// status to "assigned" as part of the same update.
//
// AI 자동매칭(COD)을 우회하는 수동 경로라, 배정 사유를 필수로 받아 백엔드가
// case_assigned_direct audit(5년 보존)에 남긴다 — COD 결정과 감사 흔적을 맞춤.

const PRIORITY_LABEL = { critical: "높음", warning: "보통", normal: "낮음" };
const PRIORITY_COLOR = { critical: "rose", warning: "amber", normal: "gray" };

// localStorage recentReasons 버킷 키. COD 거절(match_reject)과 분리.
const REASON_KIND = "case_assign_direct";
// 자주 쓰는 직접 배정 사유 — 칩 클릭으로 채워 입력 부담 완화.
const REASON_TEMPLATES = ["부하 분산(한가한 매니저 배정)", "기존 담당 연속성", "지역 근접", "긴급 수동 배정"];

export function ManagerAssignCaseModal({ manager, onClose, onAssigned }) {
  useEscapeKey(onClose);
  const [cases, setCases] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [pickId, setPickId] = React.useState(null);
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const recent = React.useMemo(() => getRecentReasons(REASON_KIND), []);

  React.useEffect(() => {
    // 응급SOS 케이스는 소방 자동연계+긴급알림으로 처리되는 별도 흐름이라
    // 케어매니저 직접 배정 대상이 아니다. COD 큐(matches#queue)와 동일하게 제외.
    const params = new URLSearchParams({ status: "matching", unassigned: "true", exclude_sos: "true" });
    if (manager?.region_id) params.set("region_id", String(manager.region_id));
    fetch(`/api/v1/cases?${params.toString()}`, { credentials: "same-origin" })
      .then(r => r.ok ? r.json() : [])
      .then(setCases)
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, [manager?.region_id]);

  const assign = async () => {
    if (!pickId) return setErr("배정할 케이스를 선택해주세요.");
    const trimmed = reason.trim();
    if (!trimmed) return setErr("직접 배정 사유를 입력해주세요. (감사 로그에 기록됩니다)");
    setErr(null); setSubmitting(true);
    try {
      const r = await fetch(`/api/v1/cases/${pickId}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ case: { care_manager_id: manager.id }, assign_reason: trimmed }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        if (r.status === 401)      setErr("로그인이 필요합니다.");
        else if (r.status === 403) setErr(j.message || "배정 권한이 없습니다.");
        else                       setErr(apiErrorMessage(j, r.status));
        return;
      }
      pushRecentReason(REASON_KIND, trimmed); // 다음 배정 때 추천으로 재사용
      onAssigned?.(j);
    } catch (e) { setErr(apiErrorMessage(e)); } finally { setSubmitting(false); }
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed', inset:0, background:'rgba(20,16,12,0.4)', display:'grid', placeItems:'center', zIndex:50}}>
      <div onClick={e=>e.stopPropagation()} className="card" style={{width:580, padding:0, maxHeight:'85vh', display:'flex', flexDirection:'column'}}>
        <div style={{padding:'16px 22px', borderBottom:'1px solid var(--border-soft)', display:'flex', alignItems:'center', justifyContent:'space-between'}}>
          <div>
            <div className="h3">케이스 배정 · {manager.name}</div>
            <div className="muted" style={{fontSize:11, marginTop:2}}>매칭 단계 케이스만 표시 · 선택 시 상태는 배정으로 전환됩니다</div>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>{Ic.close}</button>
        </div>

        {err && <div style={{margin:'14px 22px 0', padding:'10px 14px', background:'var(--rose-50)', color:'var(--rose-500)', borderRadius:10, fontSize:12.5}}>{err}</div>}

        <div style={{padding:22, overflow:'auto', flex:1}}>
          {loading && <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>불러오는 중…</div>}
          {!loading && cases.length === 0 && (
            <div className="muted" style={{padding:30, textAlign:'center', fontSize:12}}>배정 가능한 매칭 단계 케이스가 없습니다.</div>
          )}
          {!loading && cases.length > 0 && (
            <div style={{display:'flex', flexDirection:'column', gap:8}}>
              {cases.map(c => {
                const active = pickId === c.id;
                return (
                  <label key={c.id}
                    style={{display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10,
                            border: `1px solid ${active ? 'var(--orange-500)' : 'var(--border-soft)'}`,
                            background: active ? 'var(--orange-50)' : 'var(--surface)', cursor:'pointer'}}>
                    <input type="radio" name="case-pick" checked={active}
                      onChange={()=>setPickId(c.id)}
                      style={{accentColor:'var(--orange-500)'}}/>
                    <div style={{flex:1, minWidth:0}}>
                      <div style={{fontSize:13, fontWeight:700}}>
                        {c.elderly_name || `#${c.elderly_id}`}
                        <span className="mono muted" style={{marginLeft:8, fontSize:11, fontWeight:500}}>case #{c.id}</span>
                      </div>
                      {c.note && (
                        <div className="muted" style={{fontSize:11, marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{c.note}</div>
                      )}
                    </div>
                    <span className={"badge badge-" + (PRIORITY_COLOR[c.priority] || 'gray')}>{PRIORITY_LABEL[c.priority] || c.priority}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div style={{padding:'14px 22px 0', display:'grid', gap:8}}>
          <div className="label" style={{fontSize:12, fontWeight:700}}>
            직접 배정 사유 <span className="muted" style={{fontWeight:400, fontSize:10.5}}>(필수 · 감사 로그에 기록됩니다)</span>
          </div>
          <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
            {REASON_TEMPLATES.map(r => (
              <button key={r} type="button" onClick={()=>setReason(r)} title={r}
                style={{padding:'4px 10px', fontSize:11, fontWeight:600,
                        background: reason === r ? 'var(--orange-50)' : 'var(--surface)',
                        color: reason === r ? 'var(--orange-700)' : 'var(--ink-2)',
                        border: '1px solid ' + (reason === r ? 'var(--orange-500)' : 'var(--border-soft)'),
                        borderRadius: 99, cursor: 'pointer'}}>
                {r}
              </button>
            ))}
          </div>
          <textarea className="input" rows={2} value={reason}
            onChange={e=>setReason(e.target.value)}
            placeholder="템플릿을 선택하거나 직접 입력하세요 (예: 한가한 매니저 부하 분산)"
            style={{resize:'vertical', minHeight:56}}/>
          {recent.length > 0 && (
            <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
              {recent.map(r => (
                <button key={r} type="button" onClick={()=>setReason(r)} title={r}
                  style={{padding:'4px 10px', fontSize:11, fontWeight:600, background:'var(--bg-sub)', color:'var(--ink-2)',
                          border:'1px solid var(--border-soft)', borderRadius:99, cursor:'pointer',
                          maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                  {r}
                </button>
              ))}
            </div>
          )}
        </div>

        <div style={{padding:'14px 22px', borderTop:'1px solid var(--border-soft)', display:'flex', justifyContent:'flex-end', gap:8, marginTop:14}}>
          <button type="button" className="btn btn-ghost btn-md" onClick={onClose}>취소</button>
          <button type="button" className="btn btn-primary btn-md" onClick={assign} disabled={submitting || !pickId || !reason.trim()}>
            {submitting ? '배정 중…' : '배정 진행'}
          </button>
        </div>
      </div>
    </div>
  );
}
